import { DeepLink, DeepLinkResolution } from "./DeepLink";
import { DeepLinkResolver } from "./DeepLinkResolver";
import { DeepLinkStore } from "./DeepLinkStore";

/**
 * Public API facade that composes DeepLinkResolver and DeepLinkStore.
 *
 * Exports a singleton instance for use across the application.
 */
export const deepLinkService = {
  /**
   * Purely determines what the URL means. No side effects.
   *
   * @param url The URL pathname to resolve
   * @returns The resolution result (VALID with DeepLink or INVALID with reason)
   */
  resolve(url: string): DeepLinkResolution {
    return DeepLinkResolver.resolve(url);
  },

  /**
   * Persists a validated deeplink (writes sessionStorage + REQUEST_NUMBER cookie).
   *
   * @param deepLink The deeplink to persist
   */
  save(deepLink: DeepLink): void {
    DeepLinkStore.save(deepLink);
  },

  /**
   * Reads the current pending deeplink without clearing it.
   *
   * @returns The pending DeepLink, or null
   */
  get(): DeepLink | null {
    return DeepLinkStore.get();
  },

  /**
   * Returns the current pending deeplink and clears sessionStorage.
   * Does NOT clear the REQUEST_NUMBER cookie.
   *
   * @returns The pending DeepLink, or null
   */
  consume(): DeepLink | null {
    return DeepLinkStore.consume();
  },

  /**
   * Clears all pending deeplink state (sessionStorage + REQUEST_NUMBER cookie).
   */
  clear(): void {
    DeepLinkStore.clear();
  },
};

export type DeepLinkService = typeof deepLinkService;
